import { createContext } from 'react';

// Account
import type {
  GovAgencyItems,
  CurrentSelectedRole,
  ReplaceUserRoles,
  RolesToAssign,
  Unit,
} from 'modules/account/types';
import type { UserDetail } from 'modules/account/services/useGetAccountInfo';
import type { Password as AccountPassword } from 'modules/account/services/useUpdatePassword';
import type { HvsInfo } from 'modules/account/services/useGetHvsInfo';

// Suppliers
import type { GeneralDetails } from 'modules/suppliers/services/useSupplierGeneralDetails';
import type { BusinessRegistrationCertificate } from 'modules/suppliers/services/useBusinessRegistrationCertificate';
import type { ContractFromKios } from 'modules/suppliers/services/useSupplyContract';
import type { FoodSafetyCertificate } from 'modules/suppliers/services/useFoodSafetyCertificate';
import type { OtherDocument } from 'modules/suppliers/services/useOtherDocuments';

// User-info (different Password shape than account)
import type { Password as UserInfoPassword } from 'modules/user-info/services/useUpdatePasswordLoggedInUser';

// ---------------------------------------------------------------------------
// Account - TabsDataContext
// ---------------------------------------------------------------------------

export interface TabsDataContextType {
  userInfo: UserDetail;
  password: AccountPassword;
  selectedRole?: CurrentSelectedRole;
  rolesToAssign: RolesToAssign;
  /** API response GET /management/user/:userId/role - dùng cho isChecked checkbox */
  accountRoles: ReplaceUserRoles | null | undefined;
  unitAddress?: Unit;
  accountGovRoles: GovAgencyItems;
  hvsInfo?: HvsInfo;
  setUserData: (fieldName: string, value: any) => void;
  setPassword: (password: AccountPassword) => void;
  setSelectedRole: (role: CurrentSelectedRole) => void;
  setRolesToAssign: (roles: React.SetStateAction<RolesToAssign>) => void;
  handleUpdate: () => Promise<void>;
  isSaving: boolean;
  toPreviousPage: () => void;
  isCQQL: boolean;
  viewOnly: boolean;
}

export const TabsDataContext = createContext<TabsDataContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Suppliers - PanelDataContext
// ---------------------------------------------------------------------------

export interface TabsData {
  generalDetails: GeneralDetails | null;
  goodsType: GeneralDetails | null;
  businessRegistrationCertificates: BusinessRegistrationCertificate[] | null;
  supplyContract: ContractFromKios[] | null;
  certificateOfFoodSafety: FoodSafetyCertificate[] | null;
  otherDocuments: OtherDocument[] | null;
  params: {
    supplierOperationCode: string;
    customerOperationCode: string;
  };
}

export interface PanelContextType extends TabsData {
  toPreviousPage: () => void;
}

export const PanelDataContext = createContext<PanelContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// User-info - UserInfoDataContext
// ---------------------------------------------------------------------------

export interface UserInfoDataContextType {
  userInfo: UserDetail;
  password: UserInfoPassword;
  setUserData: (fieldName: string, value: any) => void;
  setPassword: (password: UserInfoPassword) => void;
  setDefaultPassword: () => void;
  handleUpdateUserInfo: () => void;
  handleUpdatePassword: () => void;
  isClient: boolean;
}

export const UserInfoDataContext = createContext<UserInfoDataContextType | undefined>(undefined);
